import { useCallback, useEffect, useState } from 'react';
import { ImageOff } from 'lucide-react';
import {
  forgetResolved,
  getSurveyPhotoRetryGeneration,
  isDisplayableSrc,
  onSurveyPhotoRetry,
  refToResolve,
  resolveSurveyPhoto,
} from './surveyPhotoUrl';

export interface SurveyPhotoImageProps {
  /** The photo's stored url (tokenless Storage link). */
  url: string | undefined | null;
  /** The photo's storage path when the caller has it (preferred over parsing the url). */
  storageRef?: string | null;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
}

/** What the last attempt for THIS storage path produced; ignored once the tile is asked to draw a different path. */
type Attempt = { ref: string; kind: 'src'; src: string } | { ref: string; kind: 'failed' };

function FailedTile({ alt, className, onRetry }: { alt: string; className?: string; onRetry?: () => void }) {
  const body = (
    <>
      <ImageOff size={18} aria-hidden="true" />
      {onRetry && <span className="text-[10px] font-semibold">Tap to retry</span>}
    </>
  );
  const classes = `${className ?? ''} flex flex-col items-center justify-center gap-1 bg-slate-100 text-slate-400`;
  if (!onRetry) return <div role="img" aria-label={`${alt || 'Photo'}: could not be shown`} className={classes}>{body}</div>;
  return (
    <button type="button" onClick={onRetry} aria-label={`${alt || 'Photo'}: could not be shown. Tap to try again`} className={classes}>
      {body}
    </button>
  );
}

/**
 * The ONE way to draw a survey photo. Reads the photo's bytes through the signed-in Storage SDK (see surveyPhotoUrl.ts) and draws them
 * from an object URL that this tile revokes when it unmounts or is handed another photo. Shows a neutral tile while it loads and a
 * tap-to-retry tile when it cannot (offline, no access): never a broken-image icon, never a throw. A tile that failed retries by
 * itself when the connection comes back or someone signs in.
 * A local preview (blob:, data:image/) or a Storage link is drawn as it is; any other address is not drawn at all.
 * What is drawn is derived from the props on every render, so a tile handed a different photo never shows the previous one.
 */
export function SurveyPhotoImage({ url, storageRef, alt, className, loading = 'lazy' }: SurveyPhotoImageProps) {
  const ref = refToResolve(url, storageRef);
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<Attempt | null>(null);

  const retry = useCallback(() => {
    if (ref) forgetResolved(ref);
    setResult(null);
    setAttempt((n) => n + 1);
  }, [ref]);

  useEffect(() => {
    if (!ref) return undefined;
    let live = true;
    let made: string | null = null;
    const startedAt = getSurveyPhotoRetryGeneration();
    resolveSurveyPhoto(ref).then(
      (blob) => {
        if (!live) return;
        made = URL.createObjectURL(blob);
        setResult({ ref, kind: 'src', src: made });
      },
      () => {
        if (!live) return;
        // A "retry now" signal (back online, someone signed in) that arrived while this read was still in flight was missed by the
        // failed-tile listener below: honour it here, once per signal.
        if (getSurveyPhotoRetryGeneration() !== startedAt) {
          forgetResolved(ref);
          setAttempt((n) => n + 1);
          return;
        }
        setResult({ ref, kind: 'failed' });
      },
    );
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
      setResult(null); // the object URL just revoked must never be matched again (photo A, then B, then A again)
    };
  }, [ref, attempt]);

  const mine = ref && result && result.ref === ref ? result : null;
  const failed = mine?.kind === 'failed';
  useEffect(() => (failed ? onSurveyPhotoRetry(retry) : undefined), [failed, retry]);

  if (!ref) {
    if (!url) return null;
    if (isDisplayableSrc(url)) return <img src={url} alt={alt} className={className} loading={loading} />;
    return <FailedTile alt={alt} className={className} />;
  }
  if (mine?.kind === 'src') {
    return (
      <img
        src={mine.src}
        alt={alt}
        className={className}
        loading={loading}
        onError={() => { forgetResolved(ref); setResult({ ref, kind: 'failed' }); }}
      />
    );
  }
  if (failed) return <FailedTile alt={alt} className={className} onRetry={retry} />;
  return alt
    ? <div role="img" aria-label={alt} aria-busy="true" className={`${className ?? ''} animate-pulse bg-slate-200`} />
    : <div aria-hidden="true" className={`${className ?? ''} animate-pulse bg-slate-200`} />;
}
